import { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMobile } from '@/hooks/useMobile';
import { getRoleColor } from '@/lib/roleColors';

interface Task {
  id: string;
  task_title: string;
  status: string;
  due_date: string | null;
  assignees: any[];
  checkpoints: any[];
  category_type: string;
  estimated_hours: number | null;
}

interface TaskCalendarViewProps {
  tasks: Task[];
  onViewDetails: (task: Task) => void;
}

export function TaskCalendarView({ tasks, onViewDetails }: TaskCalendarViewProps) {
  const { isMobile } = useMobile();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterAssignee, setFilterAssignee] = useState<string>('all');

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const daysInCalendar = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Get unique assignees across all tasks
  const allAssignees = Array.from(
    new Map(
      tasks.flatMap(t => t.assignees || []).map((a: any) => [a.id, a])
    ).values()
  );

  // Filter tasks by assignee
  const filteredTasks = filterAssignee === 'all' 
    ? tasks 
    : filterAssignee === 'unassigned'
    ? tasks.filter(t => !t.assignees || t.assignees.length === 0)
    : tasks.filter(t => t.assignees?.some((a: any) => a.id === filterAssignee));

  const getTasksForDate = (date: Date) => {
    return filteredTasks.filter(task => 
      task.due_date && isSameDay(new Date(task.due_date), date)
    );
  };

  const tasksWithoutDueDate = filteredTasks.filter(t => !t.due_date);

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  // Get tasks grouped by date for agenda view
  const getAgendaTasks = () => {
    const tasksWithDates = filteredTasks.filter(t => t.due_date);
    const grouped: Record<string, typeof tasks> = {};
    
    tasksWithDates.forEach(task => {
      const dateKey = format(new Date(task.due_date!), 'yyyy-MM-dd');
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(task);
    });
    
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 10); // Show next 10 days with tasks
  };

  return (
    <div className="bg-card rounded-lg border p-4 sm:p-6">
      {/* Header: Month Navigation & Assignee Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-bold">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          {/* Assignee Filter */}
          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger className="w-full sm:w-[180px] min-h-[44px]">
              <SelectValue placeholder="Filter by assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="unassigned">Unassigned ({tasks.filter(t => !t.assignees || t.assignees.length === 0).length})</SelectItem>
              {allAssignees.length > 0 && allAssignees.map((assignee: any) => {
                const taskCount = tasks.filter(t => t.assignees?.some((a: any) => a.id === assignee.id)).length;
                return (
                  <SelectItem key={assignee.id} value={assignee.id}>
                    {assignee.full_name} ({taskCount})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Month Navigation */}
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevMonth}
              className="min-h-[44px] min-w-[44px]"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              className="min-h-[44px]"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextMonth}
              className="min-h-[44px] min-w-[44px]"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile: Agenda View */}
      {isMobile ? (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground">Upcoming Tasks</h3>
          {getAgendaTasks().length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No upcoming tasks with due dates</p>
          ) : (
            getAgendaTasks().map(([dateKey, dateTasks]) => (
              <div key={dateKey} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={isToday(new Date(dateKey)) ? "default" : "outline"} className="text-xs">
                    {format(new Date(dateKey), 'EEE, MMM d')}
                  </Badge>
                  <span className="text-xs text-muted-foreground">({dateTasks.length} tasks)</span>
                </div>
                <div className="space-y-2 pl-2 border-l-2 border-muted">
                  {dateTasks.map(task => (
                    <div
                      key={task.id}
                      className="p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors min-h-[44px] flex items-center"
                      onClick={() => onViewDetails(task)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through opacity-70' : ''}`}>
                          {task.task_title}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{task.status.replace('_', ' ')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
          
          {/* Tasks Without Due Date */}
          {tasksWithoutDueDate.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">No Due Date ({tasksWithoutDueDate.length})</h3>
              <div className="space-y-2">
                {tasksWithoutDueDate.slice(0, 5).map(task => (
                  <div
                    key={task.id}
                    className="p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors min-h-[44px] flex items-center"
                    onClick={() => onViewDetails(task)}
                  >
                    <p className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through opacity-70' : ''}`}>
                      {task.task_title}
                    </p>
                  </div>
                ))}
                {tasksWithoutDueDate.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center">+{tasksWithoutDueDate.length - 5} more</p>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Desktop: Calendar Grid */
        <>
          <div className="grid grid-cols-7 gap-2">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-muted-foreground py-2">
                {day}
              </div>
            ))}

            {/* Date Cells */}
            {daysInCalendar.map((date, i) => {
              const dateTasks = getTasksForDate(date);
              const isCurrentDay = isToday(date);
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();

              return (
                <div
                  key={i}
                  className={`
                    min-h-[100px] border rounded-lg p-2 transition-colors
                    ${isCurrentDay ? 'bg-primary/10 border-primary border-2' : 'bg-card'}
                    ${!isCurrentMonth ? 'opacity-40' : ''}
                    hover:bg-accent/50 cursor-pointer
                  `}
                >
                  {/* Date Number */}
                  <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary' : 'text-foreground'}`}>
                    {format(date, 'd')}
                  </div>

                  {/* Task Indicators */}
                  <div className="space-y-1">
                    {dateTasks.slice(0, 3).map((task) => {
                      return (
                        <div
                          key={task.id}
                          className="text-xs p-1 rounded truncate bg-muted hover:bg-muted-foreground/20 transition-colors cursor-pointer group relative"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(task);
                          }}
                          title={task.task_title}
                        >
                          <div className="flex items-center gap-1">
                            <span className={`flex-1 truncate ${task.status === 'completed' ? 'line-through opacity-70' : ''}`}>
                              {task.task_title}
                            </span>
                            {/* Assignee Dots */}
                            <div className="flex -space-x-1">
                              {(task.assignees || []).slice(0, 3).map((assignee: any, idx: number) => {
                                const roleColors = getRoleColor(assignee.member_role);
                                return (
                                  <div
                                    key={idx}
                                    className="w-2 h-2 rounded-full border border-background"
                                    style={{ backgroundColor: roleColors.border }}
                                    title={assignee.full_name}
                                  />
                                );
                              })}
                              {(task.assignees?.length || 0) > 3 && (
                                <span className="text-[10px] ml-1">+{task.assignees.length - 3}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {dateTasks.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center font-medium">
                        +{dateTasks.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Tasks Without Due Date */}
      {tasksWithoutDueDate.length > 0 && (
        <div className="mt-6 border-t pt-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <span>📋</span>
            <span>Tasks Without Due Date</span>
            <span className="text-sm font-normal text-muted-foreground">({tasksWithoutDueDate.length})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tasksWithoutDueDate.map(task => {
              return (
                <div
                  key={task.id}
                  className="border rounded-lg p-3 hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => onViewDetails(task)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through opacity-70' : ''}`}>
                      {task.task_title}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Status: {task.status.replace('_', ' ')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
